/**
 * Project/team configuration for the Config Editor.
 *
 * This is the single source of truth for the team -> repo/path mapping. It lives in
 * config-editor-common (isomorphic) so both the browser `config-editor` plugin and the
 * node backends (`config-editor-backend`, the `ai-assistant` planner) resolve targets the
 * same way. Import it from here — there is no frontend copy or re-export shim.
 *
 * To add a new project:
 * 1. Add an entry to PROJECTS below with components, getRepo, getBasePath, hasApis, hasStepFunctions, hasLambdas, matchRepo
 * 2. Add a catalog provider in app-config.yaml under catalog.providers.github
 */

export type ConfigTargetType = 'api' | 'step-function' | 'lambda';

/**
 * GitHub organization that owns every config repo. All PROJECTS repos live under this org,
 * so the backend uses it as the `owner` when calling the GitHub API. (The frontend hardcodes
 * the same value in ConfigSelector.)
 */
export const GITHUB_OWNER = 'praetoriandigital';

export interface ProjectConfig {
  id: string;
  name: string;
  components: string[];
  getRepo: (component: string, type: ConfigTargetType) => string;
  getBasePath: (component: string, type: ConfigTargetType) => string;
  /** Whether this project exposes API configs. False for step-function-only projects (kms). */
  hasApis: boolean;
  hasStepFunctions: boolean;
  /**
   * Whether this project exposes standalone-lambda configs. Only the dedicated `phoenix-lambdas`
   * and `valkyrie-lambdas` projects set this; omitted (falsy) everywhere else. Each config.json is
   * ONE standalone Lambda.
   */
  hasLambdas?: boolean;
  /** Default branch to load in the Config Editor when none is specified. */
  defaultBranch: string;
  /**
   * Group entity refs whose members may read this project's configs. Follows the existing
   * `<team>-devops` convention. Members of FULL_ACCESS_GROUPS bypass this.
   */
  ownerGroups: string[];
  /** Match repo from entity (github.com/project-slug) and return component + path prefix */
  matchRepo: (repo: string) => { componentName: string; pathPrefix: string } | null;
  /**
   * Reverse-map a repo to its component + target type (api vs step-function).
   * Unlike matchRepo, this also matches step-function repos, so entity pages can
   * deep-link into the Config Editor for either type.
   */
  matchTarget: (repo: string) => { componentName: string; type: ConfigTargetType } | null;
}

/**
 * Templates an agent is allowed to run over MCP.
 *
 * This list IS the security boundary, and it has to be, because the catalog is not one: global
 * `catalog.rules` allow `Template`, and the team discovery providers ingest
 * `{**\/catalog-info.yaml}`, so anyone with write access to a discovered repo could publish a
 * Template with arbitrary scaffolder steps. The stock execute-template action accepts any ref in
 * the catalog, which would then run those steps with the scaffolder's GitHub App credentials.
 *
 * So the MCP surface exposes a guarded action keyed on these names instead, and the name is a
 * closed enum in its input schema — an unapproved value is rejected during validation, before any
 * code runs. Adding an entry here is a deliberate, reviewable act.
 *
 * The UI is unaffected: it still offers every Template the catalog holds.
 *
 * `phx-api-v1` is deliberately absent: the editor rejects a v1 config outright
 * ("migrate to phx-api-v2"), so letting an agent create a new one would produce a config nothing
 * can then edit.
 */
export const APPROVED_TEMPLATES = [
  { name: 'phx-api-v2', creates: 'Phoenix API (current)' },
  { name: 'condor-api-v1', creates: 'Condor API' },
  { name: 'valkyrie-api-v1', creates: 'Valkyrie API' },
  { name: 'aws-step-function-terraform', creates: 'Step function' },
  { name: 'aws-standalone-lambda-terraform', creates: 'Standalone lambda' },
] as const;

export type ApprovedTemplateName = (typeof APPROVED_TEMPLATES)[number]['name'];

/** The catalog entity ref for an approved template. */
export function templateRefFor(name: ApprovedTemplateName): string {
  return `template:default/${name}`;
}

/**
 * Groups that may read every project. The platform/DevOps team supports all of them, so scoping
 * them to one project would block their normal work.
 */
export const FULL_ACCESS_GROUPS = ['group:default/nimble-devops'];

export const PROJECTS: ProjectConfig[] = [
  {
    id: 'phoenix',
    name: 'Phoenix',
    components: ['lexid', 'lexipub', 'lexiverse', 'awb', 'lexample'],
    getRepo: (component, type) =>
      type === 'api'
        ? `phoenix_infra-${component}-apis`
        : `phoenix_infra-${component}-step-functions`,
    getBasePath: (component, type) =>
      type === 'api'
        ? `aws/components/${component}/apis`
        : `aws/components/${component}/step_functions`,
    hasApis: true,
    hasStepFunctions: true,
    defaultBranch: 'trunk',
    ownerGroups: ['group:default/phoenix-devops'],
    matchRepo: repo => {
      const m = /^phoenix_infra-(.+?)-apis$/.exec(repo);
      return m ? { componentName: m[1], pathPrefix: `aws/components/${m[1]}/apis` } : null;
    },
    matchTarget: repo => {
      // Tolerate slugs with or without the phoenix_ prefix: some catalog-info
      // annotations use "infra-<c>-step-functions" instead of "phoenix_infra-...".
      // Component still resolves correctly; getRepo rebuilds the real repo name.
      const api = /^(?:phoenix_)?infra-(.+?)-apis$/.exec(repo);
      if (api) return { componentName: api[1], type: 'api' };
      const sf = /^(?:phoenix_)?infra-(.+?)-step-functions$/.exec(repo);
      // The kms step-function repo is owned by the dedicated 'kms' project below,
      // not phoenix — don't let this permissive matcher claim it as component "kms".
      if (sf && sf[1] !== 'kms') return { componentName: sf[1], type: 'step-function' };
      return null;
    },
  },
  {
    id: 'valkyrie',
    name: 'Valkyrie',
    components: ['valkyrie'],
    getRepo: () => 'valkyrie_infra',
    getBasePath: () => 'aws/components/valkyrie/services/phoenix-account/apis',
    hasApis: true,
    hasStepFunctions: false,
    defaultBranch: 'trunk',
    ownerGroups: ['group:default/valkyrie-devops'],
    matchRepo: repo =>
      repo === 'valkyrie_infra'
        ? {
            componentName: 'valkyrie',
            pathPrefix: 'aws/components/valkyrie/services/phoenix-account/apis',
          }
        : null,
    matchTarget: repo =>
      repo === 'valkyrie_infra' ? { componentName: 'valkyrie', type: 'api' } : null,
  },
  {
    id: 'condor',
    name: 'Condor',
    components: ['condor'],
    getRepo: () => 'condor_infra-phoenix-apis',
    getBasePath: () => 'aws/components/condor/services/phoenix-account/apis',
    hasApis: true,
    hasStepFunctions: false,
    defaultBranch: 'trunk',
    ownerGroups: ['group:default/condor-devops'],
    matchRepo: repo =>
      repo === 'condor_infra-phoenix-apis'
        ? {
            componentName: 'condor',
            pathPrefix: 'aws/components/condor/services/phoenix-account/apis',
          }
        : null,
    matchTarget: repo =>
      repo === 'condor_infra-phoenix-apis' ? { componentName: 'condor', type: 'api' } : null,
  },
  {
    // KMS is a step-function-only source: these are phx-step-function configs for the
    // lexipub component, but they live in their own repo (phoenix_infra-kms-step-functions)
    // rather than the usual phoenix_infra-lexipub-step-functions. Modeled as its own project
    // so the fixed repo + lexipub base path don't collide with the phoenix component's SF repo.
    // hasApis is false: KMS has no API configs, only step functions.
    id: 'kms',
    name: 'KMS',
    components: ['lexipub-kms'],
    getRepo: () => 'phoenix_infra-kms-step-functions',
    getBasePath: () => 'aws/components/lexipub/step_functions',
    hasApis: false,
    hasStepFunctions: true,
    defaultBranch: 'trunk',
    ownerGroups: ['group:default/phoenix-devops'],
    // No API configs, so nothing for the api-only entity matcher to resolve.
    matchRepo: () => null,
    matchTarget: repo =>
      /^(?:phoenix_)?infra-kms-step-functions$/.test(repo)
        ? { componentName: 'lexipub-kms', type: 'step-function' }
        : null,
  },
  {
    // Phoenix standalone lambdas (OPS-2994). Unlike phoenix APIs/step-functions (one repo per
    // component), every component's standalone lambdas live together in the single
    // `phoenix_infra-lambdas` monorepo under aws/components/<component>/lambdas/<lambda>/config.json.
    // Each config.json is ONE standalone Lambda (phx-lambda-v1), modeled on terraform-aws-lambda v5.
    // The component is encoded in the path, so getRepo is constant and getBasePath keys off <component>.
    // Modeled as its own project (id `phoenix-lambdas`) so it can be surfaced/selected independently of
    // the phoenix API/step-function project even though it shares the phoenix component names.
    id: 'phoenix-lambdas',
    name: 'Phoenix Lambdas',
    // Standalone lambdas are limited to these real components (no `lexample` sample) — keep in
    // sync with the scaffolder template's component_name enum.
    components: ['lexid', 'lexipub', 'lexiverse', 'awb'],
    getRepo: () => 'phoenix_infra-lambdas',
    getBasePath: component => `aws/components/${component}/lambdas`,
    hasApis: false,
    hasStepFunctions: false,
    hasLambdas: true,
    defaultBranch: 'trunk',
    ownerGroups: ['group:default/phoenix-devops'],
    // Single monorepo hosting many components: the component lives in the path, so it cannot be
    // recovered from the repo name alone. Entity deep-linking (matchRepo/matchTarget) therefore
    // resolves nothing here — catalog entities carry the component in their own metadata (spec.system).
    matchRepo: () => null,
    matchTarget: () => null,
  },
  {
    // Separate from `valkyrie`, whose valkyrie_infra path is the APIs one (one path per type).
    id: 'valkyrie-lambdas',
    name: 'Valkyrie Lambdas',
    components: ['valkyrie'],
    getRepo: () => 'valkyrie_infra',
    getBasePath: () => 'aws/components/valkyrie/lambdas',
    hasApis: false,
    hasStepFunctions: false,
    hasLambdas: true,
    defaultBranch: 'trunk',
    ownerGroups: ['group:default/valkyrie-devops'],
    // valkyrie_infra deep-links to the `valkyrie` API project; claiming it here would be ambiguous.
    matchRepo: () => null,
    matchTarget: () => null,
  },
];

/**
 * Template a config belongs to when it declares neither `template_name` nor the legacy `template`.
 * Shared by the Config Editor and the MCP actions so both pick the same handler.
 */
export function defaultTemplateName(type: ConfigTargetType, projectId?: string): string {
  if (type === 'lambda') {
    return projectId === 'valkyrie-lambdas' ? 'valkyrie-lambda-v1' : 'phx-lambda-v1';
  }
  if (projectId === 'valkyrie') return 'valkyrie-api-v1';
  if (projectId === 'condor') return 'condor-api-v1';
  return 'phx-api-v2';
}

export function getProjectForComponent(component: string): ProjectConfig | undefined {
  return PROJECTS.find(p => p.components.includes(component));
}

export function getAllComponents(): string[] {
  // De-dupe: a component name can appear in more than one project (e.g. the phoenix API/step-function
  // project and the phoenix-lambdas standalone-lambda project both list `lexid`). The component
  // dropdown should show each name once.
  return [...new Set(PROJECTS.flatMap(p => p.components))].sort((a, b) => a.localeCompare(b));
}

/**
 * Reverse-map a repo (from an entity's github.com/project-slug) to the
 * Config Editor target: component, type (api vs step-function), and the
 * project's default branch. Returns null if the repo isn't a known config repo.
 */
export function getTargetFromRepo(repo: string): {
  componentName: string;
  type: ConfigTargetType;
  defaultBranch: string;
} | null {
  for (const p of PROJECTS) {
    const result = p.matchTarget(repo);
    if (result) return { ...result, defaultBranch: p.defaultBranch };
  }
  return null;
}

/**
 * Fully resolved GitHub location for a config target. This is what a node caller
 * (ConfigEditorService.listApis / fetchConfig) needs: owner, repo, base path, and the
 * project's default branch.
 */
export interface ResolvedConfigTarget {
  project: ProjectConfig;
  owner: string;
  repo: string;
  basePath: string;
  defaultBranch: string;
  hasStepFunctions: boolean;
}

/**
 * Resolve a (component, type) pair to its GitHub owner/repo/basePath. Returns null when the
 * component is unknown, when a step-function target is requested for an API-only project, or
 * when an API target is requested for a step-function-only project (kms).
 */
export function resolveConfigTarget(
  component: string,
  type: ConfigTargetType = 'api',
): ResolvedConfigTarget | null {
  const supportsType = (p: ProjectConfig): boolean => {
    if (type === 'api') return p.hasApis;
    if (type === 'step-function') return p.hasStepFunctions;
    return Boolean(p.hasLambdas);
  };
  // A component name can belong to more than one project (phoenix APIs/step-functions vs the
  // phoenix-lambdas standalone-lambda repo), so first-match by name alone isn't enough — prefer the
  // project that both owns the component AND supports the requested target type. Falls back to plain
  // name lookup so the type-mismatch guards below still return null for the api-only / sf-only cases.
  const project =
    PROJECTS.find(p => p.components.includes(component) && supportsType(p)) ??
    getProjectForComponent(component);
  if (!project) return null;
  if (type === 'api' && !project.hasApis) return null;
  if (type === 'step-function' && !project.hasStepFunctions) return null;
  if (type === 'lambda' && !project.hasLambdas) return null;
  return {
    project,
    owner: GITHUB_OWNER,
    repo: project.getRepo(component, type),
    basePath: project.getBasePath(component, type),
    defaultBranch: project.defaultBranch,
    hasStepFunctions: project.hasStepFunctions,
  };
}
